import { useEffect, useState, type ReactNode } from 'react';
import { PlainLink } from '@paige/ui';
import { useFormat } from '@paige/format';
import { IdChip } from '../ops/IdChip';
import type { FlowLinks, StopWhat } from './FlowDetail';
import { holdStory, itemKindWord, itemStateOf, sourceWord, wantsNudge } from './flow-item-words';
import type { FlowItem, FlowItemsPage, FlowItemsQuery, FlowUnit } from './flow-types';

export const ITEMS_A_PAGE = 25;

/** Which of a stage's items to list: every one, or one class and reason. */
export interface FlowItemsFilter {
  class?: FlowItem['class'];
  reason?: string;
}

interface Loaded {
  key: string;
  page: FlowItemsPage | null;
  failed: boolean;
}

/** Reads a page of items whenever the question changes; the last page stays up while the next one loads. */
function useItems(
  load: (q: FlowItemsQuery) => Promise<FlowItemsPage>,
  query: FlowItemsQuery,
): Loaded {
  const key = JSON.stringify(query);
  const [loaded, setLoaded] = useState<Loaded>({ key: '', page: null, failed: false });
  useEffect(() => {
    let current = true;
    load(JSON.parse(key) as FlowItemsQuery).then(
      (page) => current && setLoaded({ key, page, failed: false }),
      () => current && setLoaded({ key, page: null, failed: true }),
    );
    return () => {
      current = false;
    };
  }, [load, key]);
  return loaded;
}

interface ItemRowProps {
  item: FlowItem;
  unit: FlowUnit;
  links: FlowLinks;
}

/** What the item is, as an id that leads to its timeline and copies whole, and how it arrived. */
function What({ item, unit, links }: ItemRowProps): ReactNode {
  const kind = itemKindWord(item, unit);
  return (
    <td>
      <IdChip
        id={item.id}
        what={kind.toLowerCase()}
        label={`${kind} ${item.id.slice(0, 8)}`}
        href={item.timelineId ? links.timeline(item.timelineId) : undefined}
        Link={links.Link}
      />
      <span className="fl-items__sub">{sourceWord(item.source)}</span>
    </td>
  );
}

/** For a hold by design: who it waits on, that nothing is wrong, what the customer sees, and a nudge once it is old. */
function Held({ item }: { item: FlowItem }): ReactNode {
  const story = item.class === 'held' ? holdStory(item.reason) : null;
  if (!story) return null;
  return (
    <span className="fl-items__sub fl-items__held">
      {story.waitingOn}
      {story.customerSees ? ` They see: ${story.customerSees}` : ''}
      {wantsNudge(item.reason, item.ageSeconds) ? (
        <strong className="fl-items__nudge" data-nudge>
          {' '}
          {story.nudge}
        </strong>
      ) : null}
    </span>
  );
}

/** What a Stop on this item ends: its batch when it has one, else its document, else the unit; null for a capture or import. */
export function stopWhatOf(item: FlowItem): StopWhat | null {
  if (item.batchId) return { kind: 'batch', id: item.batchId };
  if (item.documentId) return { kind: 'document', id: item.documentId };
  return item.kind === 'unit' ? { kind: 'unit', id: item.id } : null;
}

/** The note and the two answers: stop it (only with a note), or keep it. */
function StopForm(props: {
  kind: StopWhat['kind'];
  onStop: (note: string) => void;
  onKeep: () => void;
}): ReactNode {
  const [note, setNote] = useState('');
  return (
    <span className="fl-items__stop-form">
      <label>
        <span className="pui-visually-hidden">Why (kept on the company's audit trail)</span>
        <input
          value={note}
          maxLength={500}
          placeholder="Why stop it"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <button
        type="button"
        disabled={note.trim().length === 0}
        onClick={() => props.onStop(note.trim())}
      >
        Stop this {props.kind}
      </button>
      <button type="button" onClick={props.onKeep}>
        Keep it
      </button>
    </span>
  );
}

/**
 * Stop: ends the item's batch (or document, or unit) after a note that stays on the company's audit trail. Offered for
 * work that is moving or waiting; parked work has Retry or resolve on Problems instead.
 */
function StopWork({
  item,
  stop,
}: {
  item: FlowItem;
  stop: NonNullable<FlowLinks['stop']>;
}): ReactNode {
  const what = stopWhatOf(item);
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  if (!what) return null;
  if (said)
    return (
      <span className="fl-items__sub" data-stopped>
        {said}
      </span>
    );
  if (!open)
    return (
      <button type="button" className="fl-items__stop" onClick={() => setOpen(true)}>
        Stop
      </button>
    );
  const send = (note: string) =>
    void stop(what, note).then(
      (r) => setSaid(`Stopped: ${r.units} steps, ${r.documents} documents. Nothing was deleted.`),
      () => setSaid('It could not be stopped. Read the list again and try once more.'),
    );
  return <StopForm kind={what.kind} onStop={send} onKeep={() => setOpen(false)} />;
}

/** One item: whose it is, what it is and its way to the timeline, how it arrived, pages, how long, and why. */
function ItemRow({ item, unit, links }: ItemRowProps): ReactNode {
  const f = useFormat();
  const Link = links.Link ?? PlainLink;
  const state = itemStateOf(item);
  return (
    <tr data-item={item.id} data-item-tone={state.tone}>
      <td>
        <Link href={links.company(item.companySlug)}>{item.companyName}</Link>
        <span className="fl-items__sub">{item.jobName ?? 'no job'}</span>
      </td>
      <What item={item} unit={unit} links={links} />
      <td className="fl-items__num">{f.int(item.pages)}</td>
      <td className="fl-items__num" title={`Here for ${f.duration(item.ageSeconds)}`}>
        {f.duration(item.ageSeconds)} ago
      </td>
      <td>
        <span className={`fl-chip fl-chip--${state.tone}`}>{state.words}</span>
        {item.words ? <span className="fl-items__sub">{item.words}</span> : null}
        <Held item={item} />
      </td>
      <td>
        {state.problems ? (
          <Link href={links.problems(item.companySlug)}>Retry or resolve</Link>
        ) : links.stop ? (
          <StopWork item={item} stop={links.stop} />
        ) : null}
      </td>
    </tr>
  );
}

function Pager({
  page,
  offset,
  onOffset,
}: {
  page: FlowItemsPage;
  offset: number;
  onOffset: (offset: number) => void;
}): ReactNode {
  const f = useFormat();
  const last = Math.min(page.total, offset + page.items.length);
  return (
    <div className="fl-items__pager">
      <span>
        {page.total === 0
          ? 'None'
          : `${f.int(offset + 1)} to ${f.int(last)} of ${f.int(page.total)}`}
      </span>
      <button
        type="button"
        disabled={offset === 0}
        onClick={() => onOffset(Math.max(0, offset - ITEMS_A_PAGE))}
      >
        Previous
      </button>
      <button
        type="button"
        disabled={last >= page.total}
        onClick={() => onOffset(offset + ITEMS_A_PAGE)}
      >
        Next
      </button>
    </div>
  );
}

/** The rows, under headings that say what each column is. */
function ItemsTable({
  items,
  unit,
  links,
}: {
  items: FlowItem[];
  unit: FlowUnit;
  links: FlowLinks;
}): ReactNode {
  return (
    <table className="fl-items__table">
      <thead>
        <tr>
          <th>Company and job</th>
          <th>What, and how it arrived</th>
          <th className="fl-items__num">Pages</th>
          <th className="fl-items__num">Waiting since</th>
          <th>State</th>
          <th>
            <span className="pui-visually-hidden">What staff can do</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <ItemRow key={`${item.kind}-${item.id}`} item={item} unit={unit} links={links} />
        ))}
      </tbody>
    </table>
  );
}

export interface FlowItemsListProps {
  place: FlowItemsQuery['place'];
  unit: FlowUnit;
  filter: FlowItemsFilter;
  company: string | null;
  /** Bumped when the picture changes, so the open list is read again. */
  stamp: string;
  load: (q: FlowItemsQuery) => Promise<FlowItemsPage>;
  links: FlowLinks;
}

/**
 * The items behind a count: what is actually at a stage, one row each, oldest first unless asked otherwise, a
 * page at a time. Every row leads to the company, to the item's timeline (its id copies whole) and, when it is parked,
 * to Problems, where staff retry or resolve it.
 */
export function FlowItemsList(props: FlowItemsListProps): ReactNode {
  const { place, unit, filter, company, stamp, load, links } = props;
  const [newest, setNewest] = useState(false);
  const [offset, setOffset] = useState(0);
  const sort = newest ? 'newest' : 'oldest';
  const paging = { sort, offset, limit: ITEMS_A_PAGE, stamp } as const;
  const { page, failed } = useItems(load, { place, ...filter, company, ...paging });
  if (failed)
    return <p className="fl-items__note">The items could not be read. Try again in a moment.</p>;
  if (!page) return <p className="fl-items__note">Reading what is here</p>;
  const turn = () => (setNewest(!newest), setOffset(0));
  return (
    <div className="fl-items" data-items={place}>
      <div className="fl-items__bar">
        <button type="button" className="fl-items__sort" onClick={turn}>
          {newest ? 'Newest first' : 'Oldest first'}
        </button>
        <Pager page={page} offset={offset} onOffset={setOffset} />
      </div>
      {page.items.length === 0 ? (
        <p className="fl-items__note">Nothing here right now.</p>
      ) : (
        <ItemsTable items={page.items} unit={unit} links={links} />
      )}
    </div>
  );
}
